/**
 * Checkout tracking utilities for legacy Mixpanel event compatibility
 *
 * This module provides type definitions and helper functions to fire Mixpanel events
 * that match the legacy website's event schema for A/B testing the checkout iframe flow.
 */

import { PlanTier, PLANS, calculateTotalPrice } from '@/app/config/plans';

// ============ Error Handling ============

/**
 * Extract a user-friendly error message from an unknown error.
 * Checks for userMessage property first, then falls back to default.
 */
export function extractErrorMessage(
  err: unknown,
  fallback = 'Payment failed. Please try again.',
): string {
  if (err && typeof err === 'object' && 'userMessage' in err) {
    return (err as { userMessage: string }).userMessage;
  }
  return fallback;
}

// ============ A/B Experiment Configuration ============

/**
 * A/B experiment configuration for the checkout iframe flow.
 * In this codebase (the iframe), we are always bucket 'b'.
 */
export const CHECKOUT_IFRAME_EXPERIMENT = {
  name: 'checkout_iframe_flow',
  bucket: 'b',
  version: 1,
} as const;

/**
 * Get the super property for the checkout iframe A/B experiment.
 * Format matches legacy: { "ab_<experiment_name>": "<bucket> (v<version>)" }
 */
export function getCheckoutExperimentSuperProp(): Record<string, string> {
  const { name, bucket, version } = CHECKOUT_IFRAME_EXPERIMENT;
  return {
    [`ab_${name}`]: `${bucket} (v${version})`,
  };
}

// ============ Legacy Type Definitions ============

/**
 * Subscription tier representing the user's PRO membership level.
 */
export type SubscriptionTier = 'pro' | 'plus' | 'free';

/**
 * Subscription level representing the billing duration.
 */
export type SubscriptionLevel = 'monthly' | '1year' | '2year' | '3year' | '5year' | 'lifetime';

/**
 * Payment method type - 'paypal' or credit card types from Braintree.
 */
// TODO: Expand with specific card types if Braintree returns stable values and we know them
export type PaymentType = 'paypal' | (string & {}); // Allow other strings while preserving autocomplete

/**
 * Membership package name sent to the backend API.
 */
export type MembershipPackageName =
  | 'standardmonthly'
  | 'proplusmonthly'
  | 'standard'
  | 'proplus'
  | 'ProPlusLifetime';

/**
 * Subscription length in years as sent to the backend. Monthly always uses value of 1.
 */
export type SubscriptionLengthYears = 1 | 2 | 3 | 5;

/**
 * Known source values that identify where the pro form was opened from.
 */
export type ProFormSource =
  | 'landing'
  | 'onboarding_journal_covers'
  | 'onboarding_reminders'
  | 'onboarding_fonts'
  | 'onboarding_pad_theme'
  | 'onboarding_page_theme'
  | 'onboarding_cover_theme'
  | 'support'
  | 'header_click'
  | 'change_plan'
  | 'prompt-browser'
  | 'upgrade_banner'
  | 'ui_themes'
  | 'gopro'
  | 'modal-pro-bar'
  | 'monthly_to_yearly'
  | 'downgrade'
  | 'new_journal'
  | 'all_journals_tags'
  | 'all_journals_sidebar'
  | 'renew'
  | 'entry_date_format'
  | 'journal_covers'
  | 'customize_entry'
  | 'additional_reminders'
  | 'pdf_export'
  | 'journal_cover_upload'
  | 'use_pro_fonts--journal'
  | 'journal_page_theme'
  | 'custom_reminder_time'
  | 'use_pro_plus_fonts--editor'
  | 'use_pro_fonts--editor'
  | 'change_entry_date'
  | 'entry_date_popup'
  | 'entry_banner'
  | 'zen_mode'
  | 'encryption_lock'
  | 'trashed_entries'
  | 'new_journal--move-entry'
  | 'entry_versions'
  | 'tagging_add_entry_tag'
  | 'entry_stars'
  | 'entry_locking'
  | (string & {}); // Allow other strings while preserving autocomplete

/**
 * Base tracking parameters for 'Pro Form View Payment' event.
 */
export interface ProFormViewPaymentParams {
  /** Source identifier for where the pro form was opened from */
  source: ProFormSource | null;
  /** Full URL path where the form was opened */
  source_path: string;
  /** Whether this is part of a new user signup flow */
  newUserSignup: boolean;
  /** Current user's ID, or null if not authenticated */
  userId: number | null;
  /** User's subscription level selection */
  selected_subscription: SubscriptionLevel;
  /** User's subscription tier selection */
  selected_tier: SubscriptionTier;
}

/**
 * Base tracking parameters for 'Pro Payment Complete' event.
 */
export interface ProPaymentCompleteParams {
  /** Total payment amount in USD, from promo or plan price. */
  payment_total: number | null;
  /** Payment method type ('paypal' or Braintree card type). */
  payment_type: PaymentType | null;
  /** Backend membership package identifier. */
  subscription_package: MembershipPackageName;
  /** Subscription duration in years (monthly = 1). */
  subscription_length: SubscriptionLengthYears;
  /** Whether this is a renewal vs new purchase. */
  renewal: boolean;
  /** User's subscription tier selection. */
  tier: SubscriptionTier | null;
  /** Combined tier and level as "{tier} {level}". */
  plan: string;
  /** Whether a promo code is being applied. */
  isPromo: boolean;
  /** The promo code string if applied. */
  promo_code: string | null;
  /** Discount amount from the promo code. */
  promo_code_discount: number | null;
  /** URL path where payment was initiated. */
  source_path: string;
  /** Source identifier (e.g., 'journal_modal', 'billing'). */
  source: ProFormSource | null;
  /** Selected tier during plan selection step. */
  selected_tier: SubscriptionTier;
}

// ============ Mapping Functions ============

/**
 * Map our PlanTier to legacy SubscriptionTier.
 * - pro, promonthly → 'pro'
 * - proplus, proplusmonthly, propluslifetime → 'plus'
 */
export function planTierToSubscriptionTier(tier: PlanTier): SubscriptionTier {
  switch (tier) {
    case 'pro':
    case 'promonthly':
      return 'pro';
    case 'proplus':
    case 'proplusmonthly':
    case 'propluslifetime':
      return 'plus';
    default:
      return 'pro';
  }
}

/**
 * Map our PlanTier and years to legacy SubscriptionLevel.
 */
export function planTierToSubscriptionLevel(tier: PlanTier, years: number): SubscriptionLevel {
  const { billingPeriod } = PLANS[tier];

  if (billingPeriod === 'monthly') return 'monthly';
  if (billingPeriod === 'lifetime') return 'lifetime';

  // Yearly plans - map years to subscription level
  const yearMap: Record<number, SubscriptionLevel> = {
    1: '1year',
    2: '2year',
    3: '3year',
    5: '5year',
  };
  return yearMap[years] ?? '1year';
}

/**
 * Map our PlanTier to legacy MembershipPackageName.
 */
export function planTierToMembershipPackage(tier: PlanTier): MembershipPackageName {
  switch (tier) {
    case 'pro':
      return 'standard';
    case 'promonthly':
      return 'standardmonthly';
    case 'proplus':
      return 'proplus';
    case 'proplusmonthly':
      return 'proplusmonthly';
    case 'propluslifetime':
      return 'ProPlusLifetime';
    default:
      return 'standard';
  }
}

/**
 * Get subscription length in years for legacy tracking.
 * Monthly plans always return 1.
 */
export function getSubscriptionLengthYears(tier: PlanTier, years: number): SubscriptionLengthYears {
  if (PLANS[tier].billingPeriod === 'monthly') return 1;

  // Map to valid subscription length values
  const validLengths: SubscriptionLengthYears[] = [1, 2, 3, 5];
  return validLengths.find((len) => years <= len) ?? 5;
}

/**
 * Build the combined "plan" property value: "{tier} {level}"
 */
export function buildPlanString(tier: PlanTier, years: number): string {
  const subscriptionTier = planTierToSubscriptionTier(tier);
  const subscriptionLevel = planTierToSubscriptionLevel(tier, years);
  return `${subscriptionTier} ${subscriptionLevel}`;
}

// ============ Event Parameter Builders ============

export interface BuildProFormViewPaymentOptions {
  tier: PlanTier;
  years: number;
  userId: number | null;
  source: string | null;
  isNewUser: boolean;
}

/**
 * Build parameters for 'Pro Form View Payment' event.
 */
export function buildProFormViewPaymentParams(
  options: BuildProFormViewPaymentOptions,
): Record<string, unknown> {
  const { tier, years, userId, source, isNewUser } = options;

  return {
    source: source as ProFormSource | null,
    source_path: typeof window !== 'undefined' ? window.location.pathname : '',
    newUserSignup: isNewUser,
    userId,
    selected_subscription: planTierToSubscriptionLevel(tier, years),
    selected_tier: planTierToSubscriptionTier(tier),
  } satisfies ProFormViewPaymentParams;
}

export interface BuildProPaymentCompleteOptions {
  tier: PlanTier;
  years: number;
  paymentType: PaymentType | null;
  promoCode: string | null;
  promoDiscount: number | null;
  source: string | null;
  isRenewal?: boolean;
}

/**
 * Build parameters for 'Pro Payment Complete' event.
 */
export function buildProPaymentCompleteParams(
  options: BuildProPaymentCompleteOptions,
): Record<string, unknown> {
  const { tier, years, paymentType, promoCode, promoDiscount, source, isRenewal = false } = options;

  const basePrice = calculateTotalPrice(tier, years);
  const subscriptionTier = planTierToSubscriptionTier(tier);

  return {
    payment_total: promoDiscount ? basePrice - promoDiscount : basePrice,
    payment_type: paymentType,
    subscription_package: planTierToMembershipPackage(tier),
    subscription_length: getSubscriptionLengthYears(tier, years),
    renewal: isRenewal,
    tier: subscriptionTier,
    plan: buildPlanString(tier, years),
    isPromo: !!promoCode,
    promo_code: promoCode,
    promo_code_discount: promoDiscount,
    source_path: typeof window !== 'undefined' ? window.location.pathname : '',
    source: source as ProFormSource | null,
    selected_tier: subscriptionTier,
  } satisfies ProPaymentCompleteParams;
}
